import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import privateApi from "../../apis/privateApi";
import Spinner from "../../components/Spinner/Spinner";
import ButtonSpinner from "../../components/ButtonSpinner/ButtonSpinner";
import Notification from "../../components/Notification/Notification";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import "./AdminUserCreate.css";

const AdminUserCreate = () => {
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [roles, setRoles] = useState([]);
    const [states, setStates] = useState([]);
    const [senatorialDistricts, setSenatorialDistricts] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [wards, setWards] = useState([]);
    const [openSection, setOpenSection] = useState(null);
    const [search, setSearch] = useState({ states: "", sd: "", lgas: "", wards: "" });
    const [notification, setNotification] = useState({ show: false, message: "", type: "info" });

    const [form, setForm] = useState({
        name: "", email: "", roleId: "", accessScope: "all", accessIds: [],
    });

    useEffect(() => {
        (async () => {
            try {
                const [rolesRes, statesRes, sdsRes, lgasRes, wardsRes] = await Promise.all([
                    privateApi.get("/api/roles"),
                    privateApi.get("/api/user/states"),
                    privateApi.get("/api/user/senatorial-districts"),
                    privateApi.get("/api/user/lgas"),
                    privateApi.get("/api/user/wards"),
                ]);
                setRoles(rolesRes.data.data || []);
                setStates(statesRes.data.data || []);
                setSenatorialDistricts(sdsRes.data.data || []);
                setLgas(lgasRes.data.data || []);
                setWards(wardsRes.data.data || []);
            } catch (err) { console.error(err); }
            finally { setInitialLoading(false); }
        })();
    }, []);

    const handleChange = (field, value) => setForm(p => ({ ...p, [field]: value }));

    const handleCheckboxChange = (id, checked) => {
        const idStr = id.toString();
        setForm(p => ({
            ...p,
            accessIds: checked
                ? [...new Set([...p.accessIds, idStr])]
                : p.accessIds.filter(x => x !== idStr),
        }));
    };

    const handleSelectAll = (items, checked) => {
        const ids = items.map(i => i._id.toString());
        setForm(p => ({
            ...p,
            accessIds: checked
                ? [...new Set([...p.accessIds, ...ids])]
                : p.accessIds.filter(id => !ids.includes(id)),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                roleId: form.roleId,
                accessScope: form.accessScope,
            };
            if (form.accessScope !== "all") {
                payload.accessIds = form.accessIds.filter(id => id && id.trim() !== "");
            }
            const res = await privateApi.post("/api/users", payload);
            setNotification({ show: true, message: res.data.message || "User created successfully", type: "success" });
            setTimeout(() => navigate("/admin/users"), 1200);
        } catch (err) {
            setNotification({ show: true, message: err?.response?.data?.message || "Failed to create user", type: "error" });
        } finally { setLoading(false); }
    };

    const renderSection = (title, items, key) => {
        if (!Array.isArray(items)) return null;
        const filtered = items.filter(i => i.name.toLowerCase().includes((search[key] || "").toLowerCase()));
        const allIds = items.map(i => i._id.toString());
        const allSelected = allIds.length > 0 && allIds.every(id => form.accessIds.includes(id));
        const isOpen = openSection === key;

        return (
            <div className={`adm-filter-box ${isOpen ? "active" : ""}`} key={key}>
                <div className="adm-filter-header" onClick={() => setOpenSection(p => p === key ? null : key)}>
                    <span>{title}</span>
                    {isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </div>
                {isOpen && (
                    <div className="adm-filter-body">
                        <input className="adm-filter-search" placeholder={`Search ${title.toLowerCase()}…`}
                            value={search[key]} onChange={e => setSearch(p => ({ ...p, [key]: e.target.value }))} />
                        <label className="adm-select-all-label">
                            <input type="checkbox" checked={allSelected}
                                onChange={e => handleSelectAll(items, e.target.checked)} />
                            Select All
                        </label>
                        <div className="adm-filter-list">
                            {filtered.map(item => {
                                const id = item._id.toString();
                                return (
                                    <label key={id} className="adm-checkbox-item">
                                        <input type="checkbox" checked={form.accessIds.includes(id)}
                                            onChange={e => handleCheckboxChange(id, e.target.checked)} />
                                        {item.name}
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (initialLoading) return <Spinner loadingInfo="Loading form data…" />;

    return (
        <div className="adm-page">
            <div className="adm-inner">
                <h1 className="adm-page-title">User Management</h1>

                <div className="adm-form-card">
                    <div className="adm-form-header">
                        <h2>Create New User</h2>
                        <p>Set up account credentials and access permissions</p>
                    </div>

                    <Notification show={notification.show} message={notification.message}
                        type={notification.type} onClose={() => setNotification(p => ({ ...p, show: false }))} />

                    <div className="adm-form-body">
                        <form onSubmit={handleSubmit} style={{ display: "contents" }}>
                            {/* Basic info */}
                            <div className="adm-form-section-title">Account Details</div>

                            <div className="adm-form-group">
                                <label className="adm-label required">Full Name</label>
                                <input className="adm-input" type="text" placeholder="Enter full name…"
                                    value={form.name} onChange={e => handleChange("name", e.target.value)} required />
                            </div>

                            <div className="adm-form-group">
                                <label className="adm-label required">Email Address</label>
                                <input className="adm-input" type="email" placeholder="Enter email address…"
                                    value={form.email} onChange={e => handleChange("email", e.target.value)} required />
                            </div>

                            <div className="adm-form-divider" />
                            <div className="adm-form-section-title">Role &amp; Access</div>

                            <div className="adm-form-group">
                                <label className="adm-label required">Role</label>
                                <select className="adm-select" value={form.roleId}
                                    onChange={e => handleChange("roleId", e.target.value)} required>
                                    <option value="">Select role…</option>
                                    {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                </select>
                            </div>

                            <div className="adm-form-group">
                                <label className="adm-label">Access Scope</label>
                                <select className="adm-select" value={form.accessScope}
                                    onChange={e => handleChange("accessScope", e.target.value)}>
                                    <option value="all">All</option>
                                    <option value="state">State</option>
                                    <option value="senatorial_district">Senatorial District</option>
                                    <option value="lga">LGA</option>
                                    <option value="ward">Ward</option>
                                </select>
                            </div>

                            {form.accessScope !== "all" && (
                                <div className="adm-access-sections">
                                    {renderSection("States", states, "states")}
                                    {renderSection("Senatorial Districts", senatorialDistricts, "sd")}
                                    {renderSection("LGAs", lgas, "lgas")}
                                    {renderSection("Wards", wards, "wards")}
                                </div>
                            )}

                            <div className="adm-form-actions">
                                <button type="button" className="adm-btn adm-btn-secondary" onClick={() => navigate(-1)}>
                                    ← Back
                                </button>
                                <button type="submit" className="adm-btn adm-btn-primary" disabled={loading}>
                                    {loading
                                        ? <ButtonSpinner size={10} textColor="#fff" loadingInfo="Creating…" />
                                        : "✓ Create User"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminUserCreate;